import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import ReactSecureStorage from 'react-secure-storage';

import clsx from 'clsx';

import { Toolbar, IconButton, Hidden } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import CssBaseline from '@material-ui/core/CssBaseline';
import List from '@material-ui/core/List';
import Drawer from '@material-ui/core/Drawer';

import HomeIcon from '@material-ui/icons/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import ImageIcon from '@material-ui/icons/Image';
import PhonelinkLockIcon from '@material-ui/icons/PhonelinkLock';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import InfoIcon from '@mui/icons-material/Info';
import ApartmentIcon from '@mui/icons-material/Apartment';

import Profile from './Profile';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  flexGrow: {
    flexGrow: 1,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
}));

const adminLinks = [
  {
    href: 'https://aiddevops.atlassian.net/jira/software/c/projects/AIDR/boards/21',
    label: 'Jira',
  },
  {
    href: 'https://aiddevops.atlassian.net/wiki/spaces/AR/overview',
    label: 'Confluence',
  },
  {
    href: 'https://rokit-aid.slack.com/archives/C01TYEDMWFL',
    label: 'Slack',
  },
  {
    href: 'https://console.cloud.google.com/welcome?hl=ko&inv=1&invt=Abn2Zw&project=rokit-pjt-dev-2022-03-21',
    label: 'Google Cloud',
  },
];

function Header({ logout, history }) {
  const memberType = ReactSecureStorage.getItem('memberType');

  const links = [
    {
      to: '/home',
      label: 'Home',
      Icon: HomeIcon,
      roles: ['ADMIN', 'DISTRIBUTOR', 'ROKIT_BD'],
    },
    { to: '/rokitAdminList', label: 'Admin', Icon: AccountCircleIcon, roles: ['ADMIN'] },
    {
      to: '/distributorAdminList',
      label: 'Distributor',
      Icon: ApartmentIcon,
      roles: ['ADMIN'],
    },
    { to: '/hospitalAdminList', label: 'Hospital', Icon: LocalHospitalIcon, roles: ['ADMIN'] },
    { to: '/template', label: 'Template', Icon: VaccinesIcon, roles: ['ADMIN'] },
    { to: '/regen', label: 'Regen File', Icon: ImageIcon, roles: ['ADMIN'] },
    { to: '/device', label: 'Device', Icon: PhonelinkLockIcon, roles: ['ADMIN'] },
    { to: '/woundMetaList', label: 'Wound Info', Icon: InfoIcon, roles: ['ADMIN'] },
    { to: '/distributorRequestList', 
      label: 'Account',
      Icon: ApartmentIcon, 
      roles: ['DISTRIBUTOR', 'ROKIT_BD'] },
    { to: '/hospitalRequestList', label: 'Hospital', Icon: LocalHospitalIcon, roles: ['DISTRIBUTOR', 'ROKIT_BD'] },
  ];

  let hasAuthRole = false;
  let hasOnlyAdminRole = false;
  let filteredLinks = null;

  if (memberType !== null) {
    hasAuthRole = memberType.some((role) =>
      ['ADMIN', 'DISTRIBUTOR', 'ROKIT_BD'].includes(role),
    );
    hasOnlyAdminRole = memberType.some((role) => ['ADMIN'].includes(role));

    if (memberType !== null) {
        filteredLinks = links.filter(link => {
          // ADMIN일 때 제외하고 싶은 링크들
          const excludeForAdmin = [
            '/distributorRequestList',
            '/hospitalRequestList'
          ];

          // memberType이 ADMIN일 때 제외할 링크가 포함되어 있으면 제외
          if (memberType.includes('ADMIN') && excludeForAdmin.includes(link.to)) {
            return false;
          }

          // 기본 필터 조건 유지
          return link.roles.some(role => memberType.includes(role));
        });
      }
    }
  
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);
  const settingRef = useRef(null);

  const [modalStatus, setModalStatus] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const handleModalClose = useCallback(() => setModalStatus(false), []);

  const showModalWithContent = useCallback(() => {
    setModalStatus(true);
  }, []);
  const logoutEvent = () => {
    logout();
    history.push('/');
  };

  useEffect(() => {
    window.addEventListener('storage', (event) => {
      if (event.key === '@secure.s.accessToken') {
        window.location.reload();
      }
    });

    function handleOutsideClick(event) {
      if (settingRef.current && !settingRef.current.contains(event.target)) {
        setSettingOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleClick = () => {
    history.push('/home');
  };

  return (
    <>
      <div style={{ marginBottom: '100px', width: '100%' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, {
            [classes.appBarShift]: open,
          })}
          style={{
            backgroundColor: 'rgb(63, 89, 140)',
            position: 'fixed'
          }}
        >
          <Toolbar className={classes.toolbar}>
          {hasAuthRole &&
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                className={clsx(classes.menuButton, open && classes.hide)}
              >
                <MenuIcon />
              </IconButton>
          }
          <div className={classes.centered} style={{ fontSize: '20px' }}>
              <div onClick={handleClick} style={{ cursor: 'pointer', textAlign: 'center' }}>
                AiD REGEN Administrator Website
              </div>
          </div>
          
            
            {hasAuthRole &&
            <button
              type="button"
              onClick={() => setSettingOpen((prev) => !prev)}
            >
              <span
              style={{color: 'white'}}
              >{ReactSecureStorage.getItem('email')}</span>
              <IconButton
                aria-label="Logout"
                style={{ color: 'white', position: 'relative' }}
                ref={settingRef}
              >
                {settingOpen ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: '35px',
                      right: '20px',
                      backgroundColor: 'white',
                      fontSize: '16px',
                      color: 'black',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: '#ffffff',
                      border: '1px solid #757575',
                      borderRadius: '15px',
                      boxShadow: '0px 4px 4px #00000040',
                      padding: '10px',
                      minWidth: '160px',
                    }}
                  >
                    {hasOnlyAdminRole &&
                      <button
                        onClick={() => {
                          showModalWithContent();
                        }}
                        style={{
                          color: '#3f598c',
                          fontWeight: '500',
                          marginBottom: '10px',
                        }}
                      >
                        My Profile
                      </button>
                    }
                    <button
                      style={{
                        color: 'red',
                        fontWeight: '500',
                      }}
                      onClick={logoutEvent}
                    >
                      Logout
                    </button>
                  </div>
                ) : null}
                <AccountCircleIcon />
              </IconButton>
            </button>
          }         
          </Toolbar>
        </AppBar>
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={open}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div className={classes.drawerHeader}>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </div>
          <Divider />
          <List style={{ fontSize: '16px' }}>
            {/* {links.map((link, index) => ( */}
            {filteredLinks && filteredLinks.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                style={{
                  color: 'rgb(63, 89, 140)',
                  textDecoration: 'none',
                }}
              >
                <ListItem button>
                  <ListItemIcon>{<Icon />}</ListItemIcon>
                  <div>{label}</div>
                </ListItem>
              </Link>
            ))}
          </List>
          <Divider />
          {hasOnlyAdminRole &&
            <List
              style={{
                fontSize: '16px',
                display: 'flex',
                flexDirection: 'column',
                padding: '10px 30px',
              }}
            >
              {adminLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  style={{
                    color: 'rgb(63, 89, 140)',
                    textDecoration: 'none',
                    padding: '5px 0px',
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </List>
          }
        </Drawer>
      </div>
      {hasAuthRole &&
        <div style={{ display: 'none' }}>
          <Profile open={modalStatus} handleModalClose={handleModalClose} />
        </div>
      }
    </>
  );
}

export default withRouter(Header);
